// ============================================================================
// Icon — redesign's stroke-based icon set (per design-handoff/primitives.jsx).
//
// String-name API so JSX reads like `<Icon name="zap" size={14} />` — same
// shape the prototype uses. Self-contained SVG (no lucide-react dep on these
// primitives) so the redesign surfaces can render cleanly even if a future
// commit drops or swaps the icon library elsewhere.
//
// Each entry is a single `d` attribute string; multi-path icons are joined
// with literal " M" segment breaks (split + re-prefixed at render time).
// ============================================================================

const ICON_PATHS = {
  activity:  'M22 12h-4l-3 9L9 3l-3 9H2',
  gauge:     'M12 14l4-4M3.34 19a10 10 0 1 1 17.32 0',
  zap:       'M13 2 3 14h9l-1 8 10-12h-9l1-8z',
  target:    'M12 12m-10 0a10 10 0 1 0 20 0a10 10 0 1 0-20 0 M12 12m-6 0a6 6 0 1 0 12 0a6 6 0 1 0-12 0 M12 12m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0',
  wallet:    'M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-3a2 2 0 0 1 0-4h4 M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-3',
  grid:      'M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z',
  star:      'M11.5 2.5l2.6 5.3 5.9.9-4.3 4.2 1 5.9-5.2-2.8-5.3 2.8 1-5.9-4.2-4.2 5.8-.9z',
  dollar:    'M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  book:      'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z',
  shield:    'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  bell:      'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9 M10.3 21a1.94 1.94 0 0 0 3.4 0',
  search:    'M11 11m-8 0a8 8 0 1 0 16 0a8 8 0 1 0-16 0 M21 21l-4.3-4.3',
  sliders:   'M4 21v-7 M4 10V3 M12 21v-9 M12 8V3 M20 21v-5 M20 12V3 M2 14h4 M10 8h4 M18 16h4',
  arrowUR:   'M7 17 17 7 M7 7h10v10',
  arrowDR:   'M7 7l10 10 M17 7v10H7',
  arrowR:    'M5 12h14 M13 5l7 7-7 7',
  arrowLeft: 'M19 12H5 M12 19l-7-7 7-7',
  chevR:     'M9 6l6 6-6 6',
  chevD:     'M6 9l6 6 6-6',
  refresh:   'M21 12a9 9 0 1 1-2.6-6.4 M21 3v6h-6',
  trendUp:   'M22 7 13.5 15.5 8.5 10.5 2 17 M16 7h6v6',
  trendDown: 'M22 17 13.5 8.5 8.5 13.5 2 7 M16 17h6v-6',
  check:     'M20 6 9 17l-5-5',
  x:         'M18 6 6 18 M6 6l12 12',
  menu:      'M3 6h18 M3 12h18 M3 18h18',
  sparkles:  'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z M19 3v4 M21 5h-4 M5 17v2 M6 18H4',
  info:      'M12 12m-10 0a10 10 0 1 0 20 0a10 10 0 1 0-20 0 M12 16v-4 M12 8h.01',
  clock:     'M12 12m-10 0a10 10 0 1 0 20 0a10 10 0 1 0-20 0 M12 6v6l4 2',
  flame:     'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.7-4 1-6 .5 2 2 3.5 3.5 5C16 4.5 16.5 6 16.5 8a4.5 4.5 0 1 1-9 0c0 1.4.3 2.5 1 3.5z',
  layers:    'M12 2 2 7l10 5 10-5-10-5z M2 12l10 5 10-5 M2 17l10 5 10-5',
};

export default function Icon({
  name,
  size = 16,
  color = 'currentColor',
  stroke = 2,
  fill = 'none',
  style,
  className,
}) {
  const d = ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      className={className}
      aria-hidden="true"
    >
      {d.split(' M').map((seg, i) => (
        <path key={i} d={(i === 0 ? seg : 'M' + seg)} />
      ))}
    </svg>
  );
}

export { ICON_PATHS };
